import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Progress,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
} from "reactstrap"
import PropTypes from "prop-types"

import {
  formatDateSimple,
  formatDate,
  timeFormatter,
  showToast,
  alarmColorsMap,
  exportPDF,
} from "utils/general"
import { allAlerts } from "constants/alerts"
import { alertsChartColors } from "./AlertsChartColors"
import html2canvas from "html2canvas"

//Excel export
import XLSX from "xlsx"

//Simple bar
import SimpleBar from "simplebar-react"

//Chart import
import ReactApexChart from "react-apexcharts"

//Translation
import { withTranslation } from "react-i18next"
import { Link } from "react-router-dom"

const AlertsChart = props => {
  const [stats, setStats] = useState({})
  const [list, setList] = useState(props.data.list)
  const [filteredList, setFilteredList] = useState(list)
  const title = props.t("Alerts")

  // Info
  const [info, setInfo] = useState(false)

  // Subcategories
  const [smallSeries, setSmallSeries] = useState()
  const [smallTitle, setSmallTitle] = useState("")
  const [smallDiagramTitles, setSmallDiagramTitles] = useState([])
  const [smallDiagramColors, setSmallDiagramColors] = useState(
    alertsChartColors.activity
  )

  // Customer stats
  const [customersData, setCustomersData] = useState()
  const [combinedData, setCombinedData] = useState()

  // Chosen content
  const [subject, setSubject] = useState(props.t("All alerts"))

  // Graph series
  const [series, setSeries] = useState()

  // Card states
  const [total, setTotal] = useState(0)
  const [closeAverage, setCloseAverage] = useState(0)
  const [openAverage, setOpenAverage] = useState(0)
  const [ackAverage, setAckAverage] = useState(0)
  const [ackMedian, setAckMedian] = useState(0)
  const [closeTimeMedian, setCloseTimeMedian] = useState(0)
  const [closeTimeAverage, setCloseTimeAverage] = useState(0)
  const [falseAck, setFalseAck] = useState(0)

  // setting details filtered data (group/customer/type)
  const [customers, setCustomers] = useState([])

  // Filtered stats by alarm types
  const [seriesDevicesCount, setSeriesDevicesCount] = useState([])
  const [seriesActivityCount, setSeriesActivityCount] = useState([])
  const [seriesApartmentCount, setSeriesApartmentCount] = useState([])
  const [seriesSafetyCount, setSeriesSafetyCount] = useState([])
  const [seriesHealthCount, setSeriesHealthCount] = useState([])
  const [seriesAbnormalCount, setSeriesAbnormalCount] = useState([])
  const [seriesOtherCount, setSeriesOtherCount] = useState([])

  //Dropdown button state
  const [menuTypes, setMenuTypes] = useState(false)
  const [menu, setMenu] = useState(false)

  // Dates
  const [timeStart] = useState(props.start)
  const [timeEnd] = useState(props.end)

  const colors = Object.values(alarmColorsMap).map(cat => cat.color)
  const colWidth = subject === props.t("All alerts") ? 6 : 12

  // Updating states
  useEffect(() => {
    if (props.data?.stats !== stats && props.data?.list !== undefined) {
      console.log("Stats: " + JSON.stringify(props.data), props.data.list)
      setStats(props.data.stats)

      //Filter by categories
      filterData(props.data.list)

      console.log("Data table alerts: ", props.data.list)
      if (props.data.list != filteredList) {
        // format alarmtype for search
        for (const element of props.data.list) {
          formatEntry(element)
        }
        setList(props.data.list)
        setFilteredList(props.data.list)
      }
      handlingCategory(subject, props.data.list, subject)
      // Set given stats
      setAckAverage(props.data.stats.ack_average)
      setAckMedian(props.data.stats.ack_median)
      setCloseTimeAverage(props.data.stats.close_average)
      setCloseTimeMedian(props.data.stats.close_median)
      setTotal(props.data.stats.total)
      setOpenAverage(props.data.stats.open)
      setFalseAck(props.data.stats.false)
      setCloseAverage(
        props.data.stats.total - props.data.stats.open - props.data.stats.false
      )
    }
  }, [props.data])

  const addCustomers = e => {
    let data = e.map(g => g.client_name)

    const withoutDuplicates = [...new Set(data)]
    setCustomers(withoutDuplicates)
  }

  // when list is updated, updates stats
  useEffect(() => {
    if (filteredList != undefined) {
      // Get groups and customers
      addCustomers(filteredList)
    }
  }, [filteredList])

  // Filter data
  const formatEntry = entry => {
    let entryFormat
    if (typeof entry.alarm_type === "string") {
      entryFormat = entry
      return entryFormat
    } else {
      entryFormat = props.t("AlarmTitles." + entry.alarm_type)
      return entryFormat
    }
  }

  //Filtering by alarmtypes
  const filterData = data => {
    // console.log("Filter data:", data)
    //DEVICES
    let devicesCount = []
    devicesCount = data.filter(d =>
      allAlerts.devices.ids.includes(d.alarm_id)
    ).length

    //APARTMENT
    let apartmentCount = []
    apartmentCount = data.filter(d =>
      allAlerts.apartment.ids.includes(d.alarm_id)
    ).length

    //SAFETY
    let safetyCount = []
    safetyCount = data.filter(d =>
      allAlerts.safety.ids.includes(d.alarm_id)
    ).length

    // HEALTH
    let healthCount = []
    healthCount = data.filter(d =>
      allAlerts.health.ids.includes(d.alarm_id)
    ).length

    //ACTIVITY
    let activityCount = []
    activityCount = data.filter(d =>
      allAlerts.activity.ids.includes(d.alarm_id)
    ).length

    //ABNORMAL
    let abnormalCount = []
    abnormalCount = data.filter(d =>
      allAlerts.ab.ids.includes(d.alarm_id)
    ).length

    //OTHER
    let otherCount = []
    otherCount = data.filter(d =>
      allAlerts.other.ids.includes(d.alarm_id)
    ).length

    // Order must be devices,apartment,safety,activity,health,abnormal,other
    setSeries([
      devicesCount,
      apartmentCount,
      safetyCount,
      activityCount,
      healthCount,
      abnormalCount,
      otherCount,
    ])

    // Different categories for small chart
    let seriesDevices = []
    for (const a of allAlerts.devices.ids) {
      const count = data.filter(d => d.alarm_id === a).length
      seriesDevices.push(count)
    }
    setSeriesDevicesCount(seriesDevices)

    let seriesActivity = []
    for (const a of allAlerts.activity.ids) {
      const count = data.filter(d => d.alarm_id === a).length
      seriesActivity.push(count)
    }
    setSeriesActivityCount(seriesActivity)

    let seriesApartment = []
    for (const a of allAlerts.apartment.ids) {
      const count = data.filter(d => d.alarm_id === a).length
      seriesApartment.push(count)
    }
    setSeriesApartmentCount(seriesApartment)

    let seriesSafety = []
    for (const a of allAlerts.safety.ids) {
      const count = data.filter(d => d.alarm_id === a).length
      seriesSafety.push(count)
    }
    setSeriesSafetyCount(seriesSafety)

    let seriesHealth = []
    for (const a of allAlerts.health.ids) {
      const count = data.filter(d => d.alarm_id === a).length
      seriesHealth.push(count)
    }
    setSeriesHealthCount(seriesHealth)

    let seriesOther = []
    for (const a of allAlerts.activity.ids) {
      const count = data.filter(d => d.alarm_id === a).length
      seriesOther.push(count)
    }
    setSeriesOtherCount(seriesOther)

    let seriesAbnormal = []
    for (const a of allAlerts.ab.ids) {
      const count = data.filter(d => d.alarm_id === a).length
      seriesAbnormal.push(count)
    }
    setSeriesAbnormalCount(seriesAbnormal)
  }

  const settingSubject = (title, type) => {
    setSubject(title)

    settingFilteredList(title, type)
  }

  // Setting CustomersData
  const handlingCategory = (ty, fl, subject) => {
    // console.log("handlingCategory", ty, fl, subject)
    let result = []
    let resultGroups = []
    let resultCombined = []
    let cus = []
    let gro = []

    cus = fl.map(g => g.client_name)
    let customers = [...new Set(cus)]

    gro = fl.map(g => g.group)
    let groups = [...new Set(gro)]

    for (const c of customers) {
      let count = fl.filter(f => f.client_name === c)
      let group = count[0].group
      let customerData = {
        name: c,
        group: group,
        type: subject,
        time1: formatDateSimple(props.start),
        time2: formatDateSimple(props.end),
        amount: count.length,
        data: count,
      }
      result.push(customerData)
    }
    for (const g of groups) {
      let count = fl.filter(f => f.group === g)
      let groupData = {
        name: g,
        type: subject,
        amount: count.length,
      }
      resultGroups.push(groupData)
    }

    for (const rg of resultGroups) {
      let rest = result.filter(s => s.group == rg.name)
      let combinedData = {
        group: rg.name,
        amount: rg.amount,
        clients: rest,
      }
      resultCombined.push(combinedData)
    }
    setCombinedData(resultCombined)
    setCustomersData(result)
  }
  const settingFilteredList = (c, type) => {
    setSmallTitle(c)
    let listValues = []
    if (type === "main") {
      // Large categories
      setSmallDiagram(c)

      let values = []

      if (c.includes(props.t("Activity"))) {
        values = allAlerts.activity.ids
        setSmallDiagramColors(alertsChartColors.activity)
      }
      if (c.includes(props.t("Abnormal activity"))) {
        values = allAlerts.ab.ids
        setSmallDiagramColors(alertsChartColors.ab)
      }
      if (c.includes(props.t("Health"))) {
        values = allAlerts.health.ids

        setSmallDiagramColors(alertsChartColors.health)
      }
      if (c.includes(props.t("Devices"))) {
        values = allAlerts.devices.ids
        setSmallDiagramColors(alertsChartColors.devices)
      }
      if (c.includes(props.t("Safety"))) {
        values = allAlerts.safety.ids
        setSmallDiagramColors(alertsChartColors.safety)
      }
      if (c.includes(props.t("Apartment"))) {
        values = allAlerts.apartment.ids
        setSmallDiagramColors(alertsChartColors.apartment)
      }
      if (c.includes(props.t("Other"))) {
        values = allAlerts.other.ids
        setSmallDiagramColors(alertsChartColors.other)
      }
      if (values != undefined || values != []) {
        let smallDiagTitles = []
        for (const element of values) {
          let title = props.t("AlarmTitles." + element)
          // Setting titles for small diagram
          smallDiagTitles.push(title)
          listValues = list.filter(data => values.includes(data.alarm_id))
        }
        setSmallDiagramTitles(smallDiagTitles)
      }
      setFilteredList(listValues)
      handlingCategory(type, listValues, c)
    }
    if (type === "sub") {
      let cus = customers

      if (cus != undefined || (cus != "" && list != undefined)) {
        listValues = list?.filter(
          l => props.t("AlarmTitles." + l.alarm_id) === c
        )
        setFilteredList(listValues)
      }
      handlingCategory(type, listValues, c)
    }
  }

  const options = {
    chart: {
      events: {
        dataPointSelection: (event, chartContext, config) => {
          setTimeout(() => {
            settingSubject(
              config.w.config.labels[config.dataPointIndex],
              "main"
            )
          }, 100)
        },
      },
    },
    tooltip: {
      enabled: true,
      fillSeriesColor: false,
      theme: "dark",
      style: {
        fontSize: "14px",
        fontFamily: undefined,
      },
    },
    id: "alarms-donut",
    labels: [
      props.t("Devices"),
      props.t("Apartment"),
      props.t("Safety"),
      props.t("Activity"),
      props.t("Health"),
      props.t("Abnormal activity"),
      props.t("Other"),
    ],
    colors: colors,
    legend: {
      show: true,
      position: "bottom",
    },
    dataLabels: {
      enabled: true,
      textAnchor: "middle",
      distributed: false,
      offsetX: 0,
      offsetY: 0,
      style: {
        fontSize: "15px",
        fontFamily: "Helvetica, Arial, sans-serif",
        fontWeight: "400",
        colors: [
          "#00000063",
          "#00000063",
          "#00000063",
          "#00000063",
          "#00000063",
          "#00000063",
        ],
      },
      background: {
        enabled: true,
        foreColor: "#fff",
        padding: 3,
        borderRadius: 2,
        borderWidth: 0,
        borderColor: "#fff",
        opacity: 0.9,
        dropShadow: {
          enabled: false,
          top: 1,
          left: 1,
          blur: 1,
          color: "#000",
          opacity: 0.45,
        },
      },
    },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            total: {
              show: true,
              label: props.t("All alerts"),
              fontSize: "15px",
              color: "#00000063",
              fontFamily: "Helvetica, Arial, sans-serif",
              fontWeight: 600,
            },
          },
        },
      },
    },
  }

  // Small chart for separated alarms
  const smallOptions = {
    chart: {
      events: {
        dataPointSelection: (event, chartContext, config) => {
          setTimeout(() => {
            settingSubject(config.w.config.labels[config.dataPointIndex], "sub")
          }, 100)
        },
      },
    },
    tooltip: {
      enabled: true,
      fillSeriesColor: false,
      theme: "dark",
      style: {
        fontSize: "14px",
        fontFamily: undefined,
      },
    },
    id: "alarms-donut-small",
    labels: smallDiagramTitles,
    colors: smallDiagramColors.fill,
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: true,
      textAnchor: "middle",
      distributed: false,
      offsetX: 0,
      offsetY: 0,
      style: {
        fontSize: "13px",
        fontFamily: "Helvetica, Arial, sans-serif",
        fontWeight: "400",
        colors: smallDiagramColors.label,
      },
      background: {
        enabled: true,
        foreColor: "#fff",
        padding: 3,
        borderRadius: 2,
        borderWidth: 0,
        borderColor: "#fff",
        opacity: 0.9,
        dropShadow: {
          enabled: false,
          top: 1,
          left: 1,
          blur: 1,
          color: "#000",
          opacity: 0.45,
        },
      },
    },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            total: {
              show: true,
              label: props.t(smallTitle),
              fontSize: "12px",
              color: "#0c0c0c",
              fontFamily: "Helvetica, Arial, sans-serif",
              fontWeight: 400,
            },
          },
        },
      },
    },
  }

  const setSmallDiagram = sub => {
    switch (sub) {
      case props.t("Devices"):
        setSmallSeries(seriesDevicesCount)
        break

      case props.t("Apartment"):
        setSmallSeries(seriesApartmentCount)
        break

      case props.t("Safety"):
        setSmallSeries(seriesSafetyCount)
        break

      case props.t("Health"):
        setSmallSeries(seriesHealthCount)
        break

      case props.t("Activity"):
        setSmallSeries(seriesActivityCount)
        break

      case props.t("Abnormal activity"):
        setSmallSeries(seriesAbnormalCount)
        break

      case props.t("Other"):
        setSmallSeries(seriesOtherCount)
        break
    }
  }

  // // Stat card values
  const callerStats = [
    {
      id: 0,
      value: openAverage,
      text: props.t("open"),
      series: toPercentage(openAverage),
    },
    {
      id: 1,
      value: closeAverage,
      text: props.t("closed"),
      series: toPercentage(closeAverage),
    },
    {
      id: 3,
      value: falseAck,
      text: props.t("false"),
      series: toPercentage(falseAck),
    },
  ]

  function toPercentage(value) {
    if (value != 0) {
      let result = Math.round((value / total) * 100)
      return [result]
    }
    if (value == 0) {
      return [0]
    }
  }

  // Calls time
  const timeStats = [
    {
      text: {
        median: props.t("Median"),
        average: props.t("Average"),
      },
      beforeHandle: {
        id: 1,
        title: props.t("Handling time"),
        valueAvg: ackAverage,
        valueMedian: ackMedian,
        formatValueAvg: timeFormatter(ackAverage),
        formatValueMedian: timeFormatter(ackMedian),
      },
      afterHandle: {
        id: 2,
        title: props.t("Handling duration"),
        valueAvg: closeTimeAverage,
        valueMedian: closeTimeMedian,
        formatValueAvg: timeFormatter(closeTimeAverage),
        formatValueMedian: timeFormatter(closeTimeMedian),
      },
    },
  ]

  // when list is updated, updates stats
  useEffect(() => {
    if (filteredList != undefined) {
      // Get groups and customers
      addCustomers(filteredList)
    }
  }, [filteredList])

  // EXPORT
  // Export PDF table
  const exportPDFTypes = () => {
    let message = props.t("PDF file exported succesfully!")
    const data = Object.values(allAlerts).map(elt => [
      props.t(elt.title),
      elt.ids.map(i => " " + props.t("AlarmTitles." + i)),
    ])
    exportPDF(title, [props.t("Alarm type"), props.t("Alert")], data, true)

    //Show toast
    showToast(message, "success", 5000, "toast-bottom-left")
  }

  const downloadExcel = () => {
    let message = props.t("Excel file exported succesfully!")
    const wb = XLSX.utils.book_new()

    const Heading = [
      [
        props.t("Start time"),
        props.t("End time"),
        props.t("Alarm type"),
        props.t("Group"),
        props.t("Name"),
        props.t("Amount"),
      ],
    ]
    const data = customersData?.map(elt => [
      elt.time1,
      elt.time2,
      elt.type,
      elt.group,
      elt.name,
      elt.amount,
    ])

    // creating sheet and adding data from 2nd row of column A.
    // Leaving first row to add Heading
    const ws = XLSX.utils.json_to_sheet(data, {
      origin: "A2",
      skipHeader: true,
    })
    XLSX.utils.sheet_add_aoa(ws, Heading, { origin: "A1" })
    XLSX.utils.book_append_sheet(wb, ws, title)
    XLSX.write(wb, { bookType: "xlsx", type: "buffer" })
    XLSX.writeFile(
      wb,
      title + " " + formatDate(timeStart) + "-" + formatDate(timeEnd) + ".xlsx"
    )

    //Show toast
    showToast(message, "success", 5000, "toast-bottom-left")
  }

  //  Download png
  const downloadPNG = async num => {
    let chartElement
    if (num === 1) {
      chartElement = document.getElementById("donut-chart1")
    } else {
      chartElement = document.getElementById("donut-chart2")
    }

    if (chartElement) {
      try {
        const canvas = await html2canvas(chartElement)
        const pngUrl = canvas.toDataURL("image/png")
        const a = document.createElement("a")
        a.href = pngUrl
        a.download = title + ".png"
        a.click()
      } catch (error) {
        console.error("Error capturing chart:", error)
      }
    }
  }

  return (
    <>
      <Modal
        isOpen={info}
        toggle={() => {
          setInfo(!info)
        }}
        centered
      >
        <ModalHeader>
          <div className="d-flex flex-wrap">
            {props.t("Alarm types")}
            <Dropdown
              id="downloadTypes"
              isOpen={menuTypes}
              toggle={() => setMenuTypes(!menuTypes)}
              className="download-btn text-center ms-2"
              type="button"
            >
              <DropdownToggle
                id="downloadstatstable"
                className="text-muted"
                tag="i"
              >
                <i className="mdi mdi-download font-size-18" />
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={() => exportPDFTypes()}>
                  {props.t("Download .pdf")}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="px-1">
            {allAlerts ? (
              <>
                {Object.values(allAlerts).map(a => (
                  <div key={a.title} className="mt-2">
                    <h6>{props.t(a.title)}</h6>
                    {a.ids.map(i => (
                      <div key={i.i} className="text-muted">
                        {props.t("AlarmTitles." + i)}
                      </div>
                    ))}
                  </div>
                ))}
              </>
            ) : (
              <></>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="float-end">
            <Button
              color="primary"
              className="ms-2"
              onClick={() => setInfo(!info)}
            >
              {props.t("Close")}
            </Button>
          </div>
        </ModalFooter>
      </Modal>
      <div id="alerts-chart">
        <Row className=" mt-2 mb-2">
          <div className="d-flex flex-wrap">
            <span>
              <h5 className="mt-1">{props.t("Alerts")}</h5>
            </span>
          </div>
        </Row>
        <Row>
          <Col lg={props.quickGuide ? 12 : 6}>
            <Card style={{ height: 127 + "px" }}>
              <CardBody>
                <div className="d-flex ">
                  <div className="avatar-md mt-2 ms-0">
                    <div className="avatar-title bg-light rounded-circle text-primary h1">
                      <i className="bx bxs-bell-ring"></i>
                    </div>
                  </div>
                  <div className="ms-4 mt-2">
                    <p className="text-muted font-size-14 mb-2">
                      {props.t("Total alarms")}
                    </p>
                    <h2 className="mb-0">{total}</h2>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={props.quickGuide ? 12 : 6}>
            {/* Duration of all calls */}
            {timeStats.map(time => (
              <div key={"div_" + time}>
                <Card style={{ height: 127 + "px" }}>
                  <CardBody>
                    <CardTitle>{props.t("Time")}</CardTitle>
                    <Row>
                      <Col xs="6" className="">
                        <h6>{time.beforeHandle.title}</h6>
                        <Row>
                          <Col xs="6">
                            <div>{time.text.average}</div>
                            <div className="font-size-14 h5">
                              {time.beforeHandle.formatValueAvg}
                            </div>
                          </Col>
                          <Col xs="6">
                            <div>{time.text.median}</div>
                            <div className="font-size-14 h5">
                              {time.beforeHandle.formatValueMedian}
                            </div>
                          </Col>
                        </Row>
                      </Col>
                      <Col xs="6" className="">
                        <h6>{time.afterHandle.title}</h6>
                        <Row>
                          <Col xs="6">
                            <div>{time.text.average}</div>
                            <div className="font-size-14 h5">
                              {time.afterHandle.formatValueAvg}
                            </div>
                          </Col>
                          <Col xs="6">
                            <div>{time.text.median}</div>
                            <div className="font-size-14 h5">
                              {time.afterHandle.formatValueMedian}
                            </div>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </div>
            ))}
          </Col>

          <Col>
            <Card style={{ height: 127 + "px" }}>
              <CardBody>
                <CardTitle>{props.t("Status")}</CardTitle>
                <div className="mt-4 mb-1">
                  <Progress multi className="progress-xl progress font-size-12">
                    <Progress
                      bar
                      color="primary"
                      className="progress-xl"
                      value={callerStats[0].series}
                    >
                      <span className="">{callerStats[0].series}%</span>
                    </Progress>
                    <Progress
                      bar
                      color="statusbar-suvantoblue"
                      className="progress-xl font-size-12"
                      value={callerStats[1].series}
                    >
                      <span className="">{callerStats[1].series}%</span>
                    </Progress>
                    <Progress
                      bar
                      color="gray"
                      className="progress-xl font-size-12"
                      value={callerStats[2].series}
                    >
                      <span className="text-dark">
                        {callerStats[2].series}%
                      </span>
                    </Progress>
                  </Progress>
                  <Row className="mt-1 font-size-13">
                    <Col xs="4">
                      <i className="fas fa-circle text-primary"></i>
                      <span className="text-muted ms-1 ">
                        {props.t("Closed")} {callerStats[0].value}
                      </span>
                    </Col>
                    <Col xs="4">
                      <i className="fas fa-circle text-statusbar-suvantoblue ms-3"></i>
                      <span className="text-muted ms-1 ">
                        {props.t("Open")} {callerStats[1].value}
                      </span>
                    </Col>
                    <Col xs="4">
                      <i className="fas fa-circle text-gray ms-3"></i>
                      <span className="text-muted ms-1 ">
                        {props.t("False")} {callerStats[2].value}
                      </span>
                    </Col>
                  </Row>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {series && (
          <Col>
            <Card>
              <CardBody>
                <Row>
                  <div className="d-flex justify-content-between align-items-center">
                    <h4 className="card-title">
                      {props.t("Alarm types")}
                      <Link
                        to="#"
                        name="infoButton"
                        id="infoButton"
                        className="ms-2 font-size-14"
                        onClick={() => setInfo(!info)}
                      >
                        <i className="bx bx-info-circle text-muted"></i>
                      </Link>
                    </h4>

                    <Dropdown
                      id="downloadxlsx"
                      isOpen={menu}
                      toggle={() => setMenu(!menu)}
                      className="download-btn text-center"
                      type="button"
                    >
                      <DropdownToggle
                        id="downloadstats"
                        className="text-muted"
                        tag="i"
                      >
                        <i className="mdi mdi-download" />
                      </DropdownToggle>
                      <DropdownMenu>
                        <DropdownItem onClick={() => downloadExcel()}>
                          {props.t("Download table")}.xlsx
                        </DropdownItem>
                        <DropdownItem onClick={() => downloadPNG(1)}>
                          {props.t("Download graph as")}
                          {smallSeries ? " 1 " : ""}
                          .png
                        </DropdownItem>
                        {smallSeries && (
                          <DropdownItem onClick={() => downloadPNG(2)}>
                            {props.t("Download graph as")} 2 .png
                          </DropdownItem>
                        )}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </Row>

                <Row>
                  <Col
                    lg={props.quickGuide ? 12 : 5}
                    xl={props.quickGuide ? colWidth : 0}
                    className="mb-3"
                  >
                    <div
                      className="mx-auto"
                      id="donut-chart1"
                      style={{
                        width: "100%",
                        maxWidth: "600px",
                        height: "100%",
                        maxHeight: "600px",
                      }}
                    >
                      <ReactApexChart
                        options={options}
                        series={series}
                        type="donut"
                        height="auto"
                        className="apex-charts apexcharts-donut"
                      />
                    </div>
                  </Col>

                  <Col
                    lg={props.quickGuide ? 12 : 7}
                    xl={props.quickGuide ? colWidth : 0}
                  >
                    {subject == props.t("All alerts") ? (
                      <Row>
                        <Col lg={3}></Col>
                        <Col lg={props.quickGuide ? 12 : 6}>
                          <h5 className="text-center">{subject}</h5>
                          <SimpleBar style={{ maxHeight: "420px" }}>
                            {combinedData?.map(el => (
                              <div key={el.group + "_div"}>
                                <div className="mt-4">
                                  <h6 className="mb-1 mt-3">
                                    {el.group}
                                    <span className="float-end me-3">
                                      {el.amount}
                                    </span>
                                  </h6>
                                  <div className="mt-2 ">
                                    {el.clients?.map(c => (
                                      <div key={c.name + "_key"}>
                                        <div className="mt-2">
                                          {c.name}
                                          <span className="float-end me-3">
                                            {c.amount}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </SimpleBar>
                        </Col>
                        <Col lg={3}></Col>
                      </Row>
                    ) : (
                      <Row>
                        <Col xs="12">
                          <h5 className="text-center">{subject} </h5>
                        </Col>
                        <Col lg="7" xs="7">
                          {smallSeries && (
                            <>
                              <div className="mt-3">
                                <Row>
                                  <Col sm="12">
                                    <div id="donut-chart2">
                                      <ReactApexChart
                                        options={smallOptions}
                                        series={smallSeries}
                                        type="donut"
                                        height="auto"
                                        className="apex-charts apexcharts-donut"
                                      />
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </>
                          )}
                        </Col>

                        <Col xs="4">
                          <SimpleBar style={{ maxHeight: "420px" }}>
                            {combinedData?.map(el => (
                              <div key={el.group + "_div"}>
                                <div className="mt-4">
                                  <h6 className="mb-1">
                                    {el.group}
                                    <span className="float-end me-3">
                                      {el.amount}
                                    </span>
                                  </h6>
                                  <div className="mt-2">
                                    {el.clients?.map(c => (
                                      <div
                                        className="mt-2 "
                                        key={c.name + "_key"}
                                      >
                                        <div>
                                          {c.name}
                                          <span className="float-end me-3">
                                            {c.amount}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </SimpleBar>
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        )}
      </div>
    </>
  )
}

AlertsChart.propTypes = {
  t: PropTypes.any,
  data: PropTypes.any,
  start: PropTypes.any,
  end: PropTypes.any,
  quickGuide: PropTypes.any,
}

export default withTranslation()(AlertsChart)
